/**
 * Message history cache reducers. A channel's history is a list of pages, newest page first; each page is
 * in ascending seq order (= ListMessagesResponse). A thread is one page: the root followed by its replies.
 * Realtime events and optimistic edits are applied here; the platform data layer stores the result.
 */
import type { Block, Member, Message, PersistentEvent, Reaction } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { displayEmoji } from "./emoji.ts";
import { inlineRuns, type RichTextContext } from "./richtext.ts";
import { isManager } from "./roles.ts";
import { isChannelLevel } from "./workspace.ts";

export type MessagePage = { messages: Message[]; hasMore: boolean };

/** Inserts or replaces by id, keeping ascending seq order. */
export function upsertMessage(list: Message[], m: Message): Message[] {
  const i = list.findIndex((x) => x.id === m.id);
  if (i >= 0) {
    const next = list.slice();
    next[i] = m;
    return next;
  }
  let at = list.length;
  while (at > 0 && list[at - 1]!.seq > m.seq) at -= 1;
  return [...list.slice(0, at), m, ...list.slice(at)];
}

export function addReaction(reactions: Reaction[], emoji: string, userId: string): Reaction[] {
  const i = reactions.findIndex((r) => r.emoji === emoji);
  if (i < 0) return [...reactions, { emoji, count: 1, userIds: [userId] }];
  const r = reactions[i]!;
  if (r.userIds.includes(userId)) return reactions;
  const next = reactions.slice();
  next[i] = { ...r, userIds: [...r.userIds, userId], count: r.userIds.length + 1 };
  return next;
}

export function removeReaction(reactions: Reaction[], emoji: string, userId: string): Reaction[] {
  const i = reactions.findIndex((r) => r.emoji === emoji);
  if (i < 0) return reactions;
  const r = reactions[i]!;
  if (!r.userIds.includes(userId)) return reactions;
  const userIds = r.userIds.filter((u) => u !== userId);
  if (userIds.length === 0) return reactions.filter((_, j) => j !== i);
  const next = reactions.slice();
  next[i] = { ...r, userIds, count: userIds.length };
  return next;
}

/** The emoji I reacted with on this message, in the message's reaction order (the hover bar's active quick reactions). */
export function myReactions(m: Message, meId: string): string[] {
  return m.reactions.filter((r) => r.userIds.includes(meId)).map((r) => r.emoji);
}

/** What pressing a reaction does (hover bar, picker or chip). */
export type ReactionToggle = {
  /** true: adds my reaction; false: I already reacted with it, so it is removed. */
  add: boolean;
  /** Polite screen-reader announcement: "Reacted with 😂" / "Removed 😂". */
  announcement: MessageDescriptor;
};

export function reactionToggle(m: Message, meId: string, emoji: string): ReactionToggle {
  const add = !myReactions(m, meId).includes(emoji);
  const shown = displayEmoji(emoji);
  return { add, announcement: msg(add ? "reaction.added" : "reaction.removed", { emoji: shown }) };
}

/**
 * Who reacted, for a chip's tooltip and accessibility label: "You and Ada reacted with 😂", "Ada, Bo and 3 others
 * reacted with 🎉". I come first; then up to two names in reaction order (unknown members by id), then a count.
 */
export function reactorsLabel(r: Reaction, members: Record<string, Member>, meId: string): MessageDescriptor {
  const emoji = displayEmoji(r.emoji);
  const name = (id: string): string => members[id]?.displayName ?? id;
  const others = r.userIds.filter((id) => id !== meId);
  const a = others[0];
  const b = others[1];
  if (r.userIds.includes(meId)) {
    if (a === undefined) return msg("reaction.who.you", { emoji });
    if (b === undefined) return msg("reaction.who.youAndOne", { name: name(a), emoji });
    return msg("reaction.who.youAndMany", { name: name(a), count: others.length - 1, emoji });
  }
  if (a === undefined) return msg("reaction.who.count", { count: r.count, emoji });
  if (b === undefined) return msg("reaction.who.one", { name: name(a), emoji });
  if (others.length === 2) return msg("reaction.who.two", { a: name(a), b: name(b), emoji });
  return msg("reaction.who.many", { a: name(a), b: name(b), count: others.length - 2, emoji });
}

/** Deleted messages keep their slot (and thread) but lose their content. */
export function tombstone(m: Message): Message {
  return { ...m, deleted: true, text: "", blocks: [], reactions: [], files: [] };
}

/** Applies `f` to the message with this id wherever it is; pages are unchanged (same object) when absent. */
/** Pins (`by` = who, at `at`) or unpins (`by` undefined) a message wherever it is in the pages (optimistic pin). */
export function pinInPages(pages: MessagePage[], messageId: string, by: string | undefined, at: number): MessagePage[] {
  return mapMessage(pages, messageId, (m) => {
    const next: Message = { ...m };
    delete next.pinnedBy;
    delete next.pinnedAt;
    if (by !== undefined) {
      next.pinnedBy = by;
      next.pinnedAt = at;
    }
    return next;
  });
}

export function mapMessage(pages: MessagePage[], messageId: string, f: (m: Message) => Message): MessagePage[] {
  let changed = false;
  const next = pages.map((p) => {
    const i = p.messages.findIndex((m) => m.id === messageId);
    if (i < 0) return p;
    changed = true;
    const messages = p.messages.slice();
    messages[i] = f(p.messages[i]!);
    return { ...p, messages };
  });
  return changed ? next : pages;
}

function contains(pages: MessagePage[], id: string): boolean {
  return pages.some((p) => p.messages.some((m) => m.id === id));
}

/** Adds a new channel-level message to the newest page (no-op when history isn't loaded or it is already there). */
export function insertIntoPages(pages: MessagePage[], m: Message): MessagePage[] {
  const first = pages[0];
  if (first === undefined) return pages;
  if (contains(pages, m.id)) return mapMessage(pages, m.id, () => m);
  return [{ ...first, messages: upsertMessage(first.messages, m) }, ...pages.slice(1)];
}

/** Message events that don't depend on where the message lives. */
function applyCommon(pages: MessagePage[], ev: PersistentEvent): MessagePage[] {
  switch (ev.type) {
    case "message.updated":
      return mapMessage(pages, ev.message.id, () => ev.message);
    case "message.deleted":
      return mapMessage(pages, ev.messageId, tombstone);
    case "reaction.added":
      return mapMessage(pages, ev.messageId, (m) => ({ ...m, reactions: addReaction(m.reactions, ev.emoji, ev.userId) }));
    case "reaction.removed":
      return mapMessage(pages, ev.messageId, (m) => ({ ...m, reactions: removeReaction(m.reactions, ev.emoji, ev.userId) }));
    default:
      return pages;
  }
}

function eventChannelId(ev: PersistentEvent): string | undefined {
  switch (ev.type) {
    case "message.created":
    case "message.updated":
      return ev.message.channelId;
    case "message.deleted":
    case "reaction.added":
    case "reaction.removed":
      return ev.channelId;
    default:
      return undefined;
  }
}

/** Applies a persistent event to one channel's history pages. */
export function applyEventToPages(pages: MessagePage[], ev: PersistentEvent, channelId: string): MessagePage[] {
  if (eventChannelId(ev) !== channelId) return pages;
  if (ev.type === "message.created") return isChannelLevel(ev.message) ? insertIntoPages(pages, ev.message) : pages;
  return applyCommon(pages, ev);
}

/** Applies a persistent event to a cached thread (root + replies). */
export function applyEventToThread(page: MessagePage, ev: PersistentEvent, rootId: string): MessagePage {
  if (ev.type === "message.created") {
    const m = ev.message;
    if (m.threadRootId !== rootId && m.id !== rootId) return page;
    return { ...page, messages: upsertMessage(page.messages, m) };
  }
  const pages = applyCommon([page], ev);
  return pages[0] === undefined ? page : pages[0];
}

/** All loaded messages in ascending order (oldest page first). */
export function flattenPages(pages: MessagePage[]): Message[] {
  const out: Message[] = [];
  for (let i = pages.length - 1; i >= 0; i--) for (const m of pages[i]!.messages) out.push(m);
  return out;
}

/** Cursor for the next older page: the smallest seq of the oldest page, or undefined when there is none. */
export function olderCursor(pages: MessagePage[]): number | undefined {
  const last = pages[pages.length - 1];
  if (last === undefined || !last.hasMore) return undefined;
  const first = last.messages[0];
  return first === undefined ? undefined : first.seq;
}

export function findInPages(pages: MessagePage[], messageId: string): Message | undefined {
  for (const p of pages) for (const m of p.messages) if (m.id === messageId) return m;
  return undefined;
}

// ---------- Previews ----------

function blockText(b: Block, ctx: RichTextContext): string {
  switch (b.type) {
    case "paragraph":
      return inlineRuns(b.children, ctx)
        .map((r) => r.text)
        .join("");
    case "code_block":
      return b.text;
    case "quote":
      return b.children.map((c) => blockText(c, ctx)).join(" ");
    case "list":
      return b.items.map((item) => item.map((c) => blockText(c, ctx)).join(" ")).join(" ");
  }
}

/**
 * A message as one line of plain text (people and channels by name, emoji as unicode, whitespace collapsed), cut to
 * `max` characters (code points) ending in "…". Without text: the first file's name, the sticker's name or the GIF's
 * title. Deleted messages have no preview ("").
 */
export function messagePreview(m: Message, ctx: RichTextContext, max: number): string {
  if (m.deleted) return "";
  let text = m.blocks
    .map((b) => blockText(b, ctx))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  if (text === "") {
    const first = m.files[0];
    text = first !== undefined ? first.name : m.sticker !== undefined ? m.sticker.name : m.gif !== undefined ? m.gif.title : "";
  }
  const chars = Array.from(text);
  if (chars.length <= max) return text;
  return `${chars.slice(0, Math.max(0, max - 1)).join("").trimEnd()}…`;
}

// ---------- Optimistic mutations (onMutate) ----------

/** Toggles my reaction locally (the server echo is idempotent). */
export function toggleReactionInPages(pages: MessagePage[], messageId: string, emoji: string, userId: string, add: boolean): MessagePage[] {
  return mapMessage(pages, messageId, (m) => ({
    ...m,
    reactions: add ? addReaction(m.reactions, emoji, userId) : removeReaction(m.reactions, emoji, userId),
  }));
}

export function editInPages(pages: MessagePage[], messageId: string, text: string, blocks: Message["blocks"], at: number): MessagePage[] {
  return mapMessage(pages, messageId, (m) => ({ ...m, text, blocks, editedAt: at }));
}

export function deleteInPages(pages: MessagePage[], messageId: string): MessagePage[] {
  return mapMessage(pages, messageId, tombstone);
}

// ---------- Editing my messages ----------

/** Where the message is: I'm still a member and the conversation isn't archived (the server refuses edits otherwise). */
export type EditPlace = { member: boolean; archived: boolean };

/**
 * I may edit this message: I wrote it (not an app), it reached the server, it isn't deleted and it isn't a sticker or
 * a GIF (those have no text), in a conversation I can still post in. Matches the server's editMessage checks.
 */
export function canEditMessage(m: Message, meId: string, sent: boolean, place: EditPlace): boolean {
  return sent && place.member && !place.archived && m.authorId === meId && m.app === undefined && !m.deleted && m.sticker === undefined && m.gif === undefined;
}

/**
 * I may delete this message: it reached the server and isn't deleted already, and I wrote it or I'm an active,
 * unrestricted owner or admin (they may delete anyone's). Matches the server's deleteMessage checks.
 */
export function canDeleteMessage(m: Message, me: Member | undefined, sent: boolean, now: number): boolean {
  if (me === undefined || !sent || m.deleted) return false;
  return m.authorId === me.userId || isManager(me, now);
}

/**
 * What saving an edit does: `refuse` (empty text — the server needs some; deleting is a separate action), `unchanged`
 * (same text: just close the editor) or `save` with the trimmed text.
 */
export function editOutcome(original: string, draft: string): { type: "refuse" } | { type: "unchanged" } | { type: "save"; text: string } {
  const text = draft.trim();
  if (text === "") return { type: "refuse" };
  if (text === original.trim()) return { type: "unchanged" };
  return { type: "save", text };
}
